import { ZoomInIcon, ZoomOutIcon } from '@change/ds-icons';

import { Button, Text } from '@change/ds-core-ui/next';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { RecommendedGroup } from 'src/app/pages/starterDashboardV2/shared/types';

import { PostPreview } from '../../../PostPreview';
import type { FullPostType } from '../../factories/types';

import { usePostPreviewContainer } from './hooks/usePostPreviewContainer';

export type PostPreviewContainerProps = {
	variant: FullPostType;
	message?: string;
	previewImageUrl?: string;
	recommendedGroup?: RecommendedGroup;
	showDisclaimer?: boolean;
	petition?: StarterDashboardV2PageData['petition'];
	isLoading: boolean;
};

export function PostPreviewContainer(props: PostPreviewContainerProps) {
	const {
		data: { isMobile, isExpanded },
		actions: { toggleExpanded },
	} = usePostPreviewContainer();

	const { message, previewImageUrl, variant, recommendedGroup, showDisclaimer, petition, isLoading } = props;

	const sharingResource = {
		message: message || '',
		previewImageUrl: previewImageUrl || '',
		recommendedGroup,
	};

	return (
		<section className="bg-background-overlay rounded-large">
			{!isLoading && (
				<div className="relative">
					<div className="absolute right-4 top-4">
						{/* Expand feature is only available in mobile */}
						{isMobile && (
							<Button
								data-qa="expand-post-preview-button"
								data-testid="expand-post-preview-button"
								aria-label="Expand preview"
								variant="secondary-solid"
								size="x-small"
								onClick={toggleExpanded}
								leadingIcon={
									isExpanded ? (
										<ZoomInIcon data-testid="zoom-in-icon" size="small" />
									) : (
										<ZoomOutIcon data-testid="zoom-out-icon" size="small" />
									)
								}
								iconOnly
							/>
						)}
					</div>
				</div>
			)}
			<div className="flex flex-col items-center gap-3 px-12 py-4 md:px-8">
				<PostPreview
					variant={variant}
					size={isExpanded ? 'medium' : 'small'}
					showPlatformIcon={false}
					loading={isLoading}
					sharingResource={sharingResource}
					petition={petition}
				/>
				{showDisclaimer && (
					<Text data-testid="disclaimer-text" className="text-size-body-small">
						{isLoading ? 'Your custom post is generating...' : 'Content for your post is AI generated'}
					</Text>
				)}
			</div>
		</section>
	);
}
